import {
    doc,
    onSnapshot,
    type SnapshotListenOptions,
} from 'firebase/firestore';
import type { NavigateFunction } from 'react-router-dom';
import { sharedConfig } from '../base-config';
import { logger } from '../log';
import { itemService } from '../services';
import { compareFirestoreDataWithLocal } from '../sync';
import {
    getStoredTimestamp,
    REGISTER_TRACKER_TIMESTAMPS_KEY,
    type RegisterChangeTrackerTimestamps,
    setStoredTimestamp,
} from '../timestamps';
import { unpairAndClearUserInfo } from '../unpair';
import { firestore } from './firestore';
import type { RegisterEventTrackerData, StockAdjustmentEvent } from './types';

export class FirestoreRegisterEventTracker {
    subscribe(
        registerId: string,
        navigate: NavigateFunction,
        enableNewStockTracking?: boolean,
    ) {
        const trackerKey = sharedConfig.REGISTER_EVENT_TRACKER_KEY;
        const document = `${trackerKey}/${registerId}`;

        const options: SnapshotListenOptions = { includeMetadataChanges: true };

        return onSnapshot(doc(firestore, document), options, async (doc) => {
            const remoteData = (doc.data() ?? []) as RegisterEventTrackerData;

            if (doc.metadata.fromCache) {
                return;
            }

            if (remoteData.registerUnpaired) {
                logger.warn('[UNPAIR]: Firestore triggered register unpair.');
                await unpairAndClearUserInfo(navigate);
                return;
            }

            if (enableNewStockTracking) {
                return;
            }

            if (!remoteData.stockAdjustment) {
                return;
            }

            const stockAdjustmentEvent = JSON.parse(
                remoteData.stockAdjustment,
            ) as StockAdjustmentEvent;

            const local = (await getStoredTimestamp(
                REGISTER_TRACKER_TIMESTAMPS_KEY,
            )) as RegisterChangeTrackerTimestamps;

            const hasNewData = compareFirestoreDataWithLocal(
                stockAdjustmentEvent.updatedAt,
                local.stockAdjustment,
            );

            if (!hasNewData) {
                return;
            }

            await itemService.updateVariantStock(stockAdjustmentEvent);

            const newTimestamp = {
                stockAdjustment: stockAdjustmentEvent.updatedAt,
            };

            await setStoredTimestamp(
                newTimestamp,
                REGISTER_TRACKER_TIMESTAMPS_KEY,
            );
        });
    }
}

const firestoreRegisterEventTracker = new FirestoreRegisterEventTracker();
export { firestoreRegisterEventTracker };
